import React, { useState } from 'react';
import { SrmProject, Page, NavParams } from '@types';
import SrmModuleReviewModal from '../SrmModuleReviewModal';
import { isSrmAutomatedWorkflow, srmModuleReportData, srmModuleReportGaps, srmModuleReportText } from '../../../functions/src/common/srmModuleReports';
import { cleanSrmSummary } from '../../common/srmSummary';
import { redactDailyMicroReport } from '../../utils/dailyMicroRedaction';

interface SrmBlueprintDisplayProps {
    result: SrmProject;
    onNavigate?: (nav: { page: Page; params?: NavParams }) => void;
}

const SrmBlueprintDisplay: React.FC<SrmBlueprintDisplayProps> = ({ result, onNavigate }) => {
    const [selectedReviewModule, setSelectedReviewModule] = useState<any>(null);

    return (
        <div className="space-y-6 max-w-5xl mx-auto py-8">
            <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-slate-200 shadow-sm">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-black rounded uppercase tracking-wider">
                            SRM Framework Review
                        </span>
                        {result.projectNumber && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-mono rounded border border-slate-200">
                                ID: {result.projectNumber}
                            </span>
                        )}
                    </div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">Framework Blueprint: {result.name}</h1>
                    <p className="text-sm text-slate-500 font-medium">Strategic Security Risk Management Architecture • {result.locationName}</p>
                </div>
            </div>

            <div className="analysis-screen-only bg-blue-600 rounded-xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl group-hover:bg-white/20 transition-all duration-700"></div>
                <div className="relative z-10">
                    <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                        Framework Review
                    </h2>
                    <p className="text-blue-100 max-w-lg">
                        Review the generated facility reports and plans. Complete flagged site audit inputs and retry reports with missing data from the project dashboard.
                    </p>
                </div>
                <button 
                    onClick={() => onNavigate?.({ page: 'srm_command_center', params: { view: 'dashboard', projectId: result.id } })}
                    className="relative z-10 bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 rounded-xl font-black transition-all shadow-xl hover:-translate-y-1 active:translate-y-0"
                >
                    Move to SRM Project Management Dashboard
                </button>
            </div>

            <div className="analysis-screen-only grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest pl-2">AI-Automated Insights</h3>
                    {result.modules.filter(m => isSrmAutomatedWorkflow(m.workflowId)).map(m => (
                        <div key={m.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 transition-all group">
                            <div className="flex justify-between items-start mb-3">
                                <h4 className="font-bold text-slate-800">{m.title}</h4>
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[9px] font-black rounded uppercase tracking-tighter">{m.status}</span>
                            </div>
                            <p className="text-xs text-slate-500 mb-4 line-clamp-2">{m.description}</p>
                            {srmModuleReportGaps(m).map(gap => <p key={gap} role="status" className="mb-3 text-xs text-amber-700">{gap}</p>)}
                            <button 
                                onClick={() => setSelectedReviewModule(m)}
                                className="w-full py-2 bg-slate-50 hover:bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-slate-200 group-hover:border-blue-200 transition-all flex items-center justify-center gap-2"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                Review Insights
                            </button>
                        </div>
                    ))}
                </div>

                <div className="space-y-4">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest pl-2">User-Required Actions</h3>
                    {result.modules.filter(m => !isSrmAutomatedWorkflow(m.workflowId)).map(m => (
                        <div key={m.id} className="bg-slate-50 p-5 rounded-xl border border-slate-200 opacity-80">
                            <div className="flex justify-between items-start mb-3">
                                <h4 className="font-bold text-slate-600">{m.title}</h4>
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-black rounded uppercase tracking-tighter">Required</span>
                            </div>
                            <p className="text-xs text-slate-400 mb-4 line-clamp-2">{m.description}</p>
                            {srmModuleReportGaps(m).map(gap => <p key={gap} role="status" className="mb-3 text-xs text-amber-700">{gap}</p>)}
                            <div className="flex items-center gap-2 text-[10px] font-bold text-slate-400 italic">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                Action Unlockable in Dashboard
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="analysis-print-only space-y-8">
                <section>
                    <h2 className="mb-4 text-xl font-bold text-slate-900">Executive Summary</h2>
                    <div className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{cleanSrmSummary(result.summary)}</div>
                </section>
                {result.modules.map(module => {
                    const report = srmModuleReportData(module.resultData ?? (module as any).data ?? (module as any).result);
                    const printable = module.workflowId === 'daily_micro_report' ? { ...module, resultData: redactDailyMicroReport(report) } : module;
                    return <section key={module.id}>
                        <h2 className="mb-4 text-xl font-bold text-slate-900">{module.title}</h2>
                        <div className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{srmModuleReportText(printable)}</div>
                    </section>;
                })}
            </div>

            {selectedReviewModule && (
                <SrmModuleReviewModal 
                    isOpen={!!selectedReviewModule}
                    onClose={() => setSelectedReviewModule(null)}
                    onApprove={() => {
                        setSelectedReviewModule(null);
                    }}
                    moduleName={selectedReviewModule.title}
                    resultData={srmModuleReportData(selectedReviewModule.resultData ?? selectedReviewModule.data ?? selectedReviewModule.result)}
                    workflowId={selectedReviewModule.workflowId}
                    reportGaps={srmModuleReportGaps(selectedReviewModule)}
                    readOnly
                />
            )}
        </div>
    );
};

export default SrmBlueprintDisplay;
